import React from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";

import Logo from "../../../components/Logo";
import Brand from "../../../components/Brand";
import brand from "../../../inc/branding.json";

const LAST_UPDATED = "February 17, 2026";

const Section = ({ title, children }) => (
  <Box sx={{ textAlign: "left" }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: "left" }}>
      {title}
    </Typography>
    <Box
      sx={{
        color: "text.secondary",
        "& p": { mb: 1.5, textAlign: "justify", textJustify: "inter-word" },
        "& ul": { mt: 0.5, mb: 1.5, pl: 2.5 },
        "& li": { textAlign: "left" },
      }}
    >
      {children}
    </Box>
  </Box>
);

const Privacy = () => {
  const dba = brand.brandConfig?.config?.dba;
  const legalName = brand.brandConfig?.config?.name;
  const legalConfig = brand.brandConfig?.config?.legal || {};
  const legalEmail =
    brand.brandConfig?.config?.legal_email ||
    brand.brandConfig?.config?.support_email;
  const privacyContactName =
    legalConfig.privacy_contact_name || "Privacy Team";
  const privacyContactEmail =
    legalConfig.privacy_contact_email || legalEmail;
  const subprocessorsUrl = legalConfig.subprocessors_url || "";
  const retentionPolicyUrl = legalConfig.retention_policy_url || "";
  const address = brand.brandConfig?.config?.address || {};
  const legalAddress = [
    address.line1,
    address.line2,
    [address.city, address.state, address.postal_code].filter(Boolean).join(" "),
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 6 }, bgcolor: "grey.50" }}>
      <Container maxWidth="md">
        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Logo size={34} />
              <Brand width={111} height={48} />
            </Stack>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Privacy Policy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated: {LAST_UPDATED}
              </Typography>
            </Box>

            <Section title="Important Notice">
              <p>
                This Privacy Policy explains how {legalName} ("Company", "we", "us", "our") processes personal data in
                connection with {dba}. This document is a platform-ready legal draft and should be reviewed by legal counsel
                before production use.
              </p>
            </Section>

            <Section title="1. Scope and Controller Information">
              <p>
                This Policy applies to users, visitors, clients, contractors, and admins using the Services. Unless otherwise
                stated, {legalName} is the data controller for personal data processed under this Policy.
              </p>
            </Section>

            <Section title="2. Categories of Personal Data">
              <ul>
                <li>Identity and contact data (name, email, profile data, account role).</li>
                <li>Operational data (hire requests, shortlist records, status and audit events, notifications).</li>
                <li>Work and billing data (work logs, timesheets, invoices, payment-related metadata).</li>
                <li>Security and device data (sign-in logs, user agent, IP metadata where available).</li>
                <li>Support and communication data (tickets, inquiries, administrative communications).</li>
              </ul>
            </Section>

            <Section title="3. Sources of Data">
              <ul>
                <li>Directly from you during onboarding, profile updates, and platform activity.</li>
                <li>From your organization or authorized admins using operational workflows.</li>
                <li>From integrated service providers (e.g., payment or identity providers).</li>
                <li>Automatically from your use of the Services and connected devices.</li>
              </ul>
            </Section>

            <Section title="4. Purposes and Legal Bases for Processing">
              <ul>
                <li>To provide and perform Services (contract performance).</li>
                <li>To secure accounts and prevent abuse/fraud (legitimate interests/legal obligations).</li>
                <li>To process billing and payment operations (contract/legal obligations).</li>
                <li>To operate staffing workflow, matching, and profile generation (contract/legitimate interests).</li>
                <li>To comply with law, enforcement requests, and regulatory requirements (legal obligations).</li>
              </ul>
            </Section>

            <Section title="5. AI and Automated Processing">
              <p>
                We may use AI systems to generate profile summaries or operational insights from records you submit, including
                experience entries and approved/paid work history. AI outputs may be incomplete or inaccurate and are subject to
                human review and override where implemented.
              </p>
            </Section>

            <Section title="6. Disclosures and Recipients">
              <ul>
                <li>Authorized users based on role-based permissions and workflow requirements.</li>
                <li>Service providers/subprocessors (cloud hosting, analytics, communications, payment infrastructure).</li>
                <li>Professional advisors, auditors, insurers, and legal authorities where required.</li>
                <li>Acquirer/successor entities as part of corporate transactions, subject to safeguards.</li>
              </ul>
              {subprocessorsUrl ? (
                <p>
                  Current subprocessors are listed at{" "}
                  <a href={subprocessorsUrl} target="_blank" rel="noreferrer">
                    {subprocessorsUrl}
                  </a>.
                </p>
              ) : null}
            </Section>

            <Section title="7. Payment and Identity Providers">
              <p>
                Payment processing and payouts rely on third-party providers, including Stripe. Identity, authentication, and
                infrastructure operations rely on third-party cloud services, including Google Cloud and Firebase. Provider terms
                and privacy notices may apply in addition to this Policy.
              </p>
            </Section>

            <Section title="8. International Data Transfers">
              <p>
                Personal data may be transferred to and processed in countries other than your own. Where required, we implement
                lawful transfer safeguards (such as contractual clauses or equivalent mechanisms).
              </p>
            </Section>

            <Section title="9. Data Retention">
              <p>
                We retain personal data for as long as needed to provide Services, enforce agreements, resolve disputes, satisfy
                legal obligations, and maintain security/audit records. Retention periods vary by data category and jurisdiction.
              </p>
              {retentionPolicyUrl ? (
                <p>
                  For additional detail, see our retention policy:{" "}
                  <a href={retentionPolicyUrl} target="_blank" rel="noreferrer">
                    {retentionPolicyUrl}
                  </a>.
                </p>
              ) : null}
            </Section>

            <Section title="10. Security Measures">
              <p>
                We use administrative, technical, and organizational safeguards designed to protect data, including role-based
                access controls, authentication protections, and managed cloud security controls. No system is completely secure.
              </p>
            </Section>

            <Section title="11. Your Privacy Rights">
              <p>Depending on your jurisdiction, you may have rights to:</p>
              <ul>
                <li>Access, correct, or delete personal data.</li>
                <li>Restrict or object to certain processing activities.</li>
                <li>Receive data portability where applicable.</li>
                <li>Withdraw consent where processing relies on consent.</li>
                <li>Lodge complaints with a competent supervisory authority.</li>
              </ul>
              <p>Requests can be submitted via support or legal contact channels.</p>
            </Section>

            <Section title="12. Cookies and Similar Technologies">
              <p>
                We may use cookies/local storage and similar technologies for authentication, session continuity, security, and
                functional analytics. Browser controls may allow you to manage certain cookie settings.
              </p>
            </Section>

            <Section title="13. Children’s Privacy">
              <p>
                The Services are not intended for children under 18. We do not knowingly collect personal data from children.
              </p>
            </Section>

            <Section title="14. U.S. State Privacy Disclosures (If Applicable)">
              <p>
                For residents of U.S. states with privacy laws, additional rights and disclosures may apply, including rights to
                know/access, delete, correct, and opt out of certain data uses. We do not sell personal data as "sale" is
                commonly defined in privacy statutes unless explicitly disclosed.
              </p>
            </Section>

            <Section title="15. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Material changes will be posted with a new effective date.
                Continued use after the effective date constitutes acceptance of the updated Policy.
              </p>
            </Section>

            <Section title="16. Contact and Data Requests">
              <p>
                For privacy requests or questions, contact {privacyContactName} at{" "}
                <a href={`mailto:${privacyContactEmail}`}>{privacyContactEmail}</a>.
              </p>
              {legalAddress ? <p>Mailing address: {legalAddress}.</p> : null}
            </Section>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default Privacy;
